import {useCallback, useMemo, useState} from 'react';
import assert from 'assert-ts';
import {useNavigate} from 'react-router';
import {Concept} from 'types';
import {EntitySearchState} from 'feature/linkedValueSearch/context/types';
import type {VerifiedLinkedCatalogPostLink} from 'schemaDefinition/types';
import type {ManifestationV4} from 'services/metadata/types';
import {useLocalization} from 'localization';
import {
  createChangeRequestFromMove,
  saveChangeRequestInLocalStore,
} from 'api/localStorage';
import {
  metadataEditNewChangeRequestFromCopy,
  selectMetadataEditOrder,
  useAssertSavedWork,
  useMoveManifestationToExistingWorkMutation,
  useOptionalExpression,
  useUniqueSavedManifestation,
} from 'services/metadata';
import {useAppSelector, useAppStore} from 'store/hooks';
import {useSnacks} from 'components';
import type {AlertDialogAsPromiseV3} from 'components/alertdialog/hooks/useAlertDialogAsPromiseV3';
import {useAlertDialogAsPromiseV3} from 'components/alertdialog/hooks/useAlertDialogAsPromiseV3';
import {useLinkedValueSearchFieldContext} from 'sceneExtensions';
import {MoveToExistingWorkInfo} from '../components/MoveToExistingWorkInfo';
import {copyFromOtherWork, getManifestationCountInWork} from '../functions';
import {useCleanupEmptyWorkAfterMove} from './useCleanupEmptyWorkAfterMove';

/**
 * Use to move manifestation to other work (target work), either
 * explicit manifestation id on source work (current edit context)
 * or implicit when source work has just one manifestation
 */
export const useMoveToOrCopyFromOtherWork = (
  manifestationId?: string,
): {
  openSearch: () => void;
  ConfirmMoveToOther: AlertDialogAsPromiseV3;
  isConfirmOpen: boolean;
  isConfirmClosing: boolean;
} => {
  const {t} = useLocalization();
  const {errorSnack} = useSnacks();
  const navigate = useNavigate();
  const {dispatch, getState} = useAppStore();
  const work = useAssertSavedWork();
  const manifestation = useUniqueSavedManifestation(manifestationId);
  const expression = useOptionalExpression(manifestation?.expressionId);

  const expressionOrder = useAppSelector(selectMetadataEditOrder);
  const manifestationCount = getManifestationCountInWork(expressionOrder);
  const workEmptyAfterMove = manifestationCount === 1;
  const [cleanupEmptyWork, setCleanupEmptyWork] = useState(workEmptyAfterMove);
  const {clean} = useCleanupEmptyWorkAfterMove(work.id);
  const [moveManifestationToExistingWorkMutation] =
    useMoveManifestationToExistingWorkMutation();

  const {
    pleaseConfirm: pleaseConfirmMove,
    AlertDialog: ConfirmMoveToOther,
    isOpen,
    isClosing,
  } = useAlertDialogAsPromiseV3();

  const context = useLinkedValueSearchFieldContext();

  const handleMove = useCallback(
    (
      targetWork: VerifiedLinkedCatalogPostLink,
      manifestation: ManifestationV4,
    ) => {
      const targetWorkId = targetWork.entityId;
      pleaseConfirmMove(
        () => {
          return moveManifestationToExistingWorkMutation({
            manifestationId: manifestation.id,
            targetWorkId,
          })
            .then(() => {
              if (workEmptyAfterMove && cleanupEmptyWork) {
                clean();
              }

              const changeRequest = createChangeRequestFromMove(
                targetWorkId,
                work,
                manifestation,
              );
              saveChangeRequestInLocalStore(changeRequest);
              navigate(
                `/metadata/${targetWorkId}?manifestationId=${manifestation.id}`,
              );
            })
            .catch(err => {
              errorSnack(t('search.work.move.failed'), err);
              throw err;
            });
        },
        {
          title: t('search.work.move.title'),
          body: (
            <MoveToExistingWorkInfo
              manifestation={manifestation}
              currentExpression={assert(expression)}
              targetWork={assert(targetWork.entityValue, 'targetWork')}
              cleanupEmptyWorkInitialState={workEmptyAfterMove}
              onSetCleanupEmptyWork={
                workEmptyAfterMove
                  ? (value: boolean) => setCleanupEmptyWork(value)
                  : undefined
              }
            />
          ),
          okTitle: t('search.work.move.ok'),
          okIcon: 'Move',
          cancelTitle: t('general.cancel'),
        },
      );
    },
    [
      clean,
      cleanupEmptyWork,
      errorSnack,
      expression,
      moveManifestationToExistingWorkMutation,
      navigate,
      pleaseConfirmMove,
      t,
      work,
      workEmptyAfterMove,
    ],
  );

  const handleCopy = useCallback(
    (
      targetWork: VerifiedLinkedCatalogPostLink,
      manifestation?: ManifestationV4,
    ) => {
      const dummyManifestation = assert(
        getState().metadataEdit?.savedMetadata?.manifestations[0],
      );
      const manifestationInfo = manifestation
        ? ({
            toManifestation: manifestation,
          } as const)
        : {
            toManifestationPlaceholder: {
              id: dummyManifestation.id,
              expressionId: dummyManifestation.expressionId,
            } as const,
          };

      const changeRequest = copyFromOtherWork({
        toWork: work,
        ...manifestationInfo,
        fromWork: assert(
          targetWork.entityValue?.work,
          'useMoveToOrCopyFromOtherWork: fromWork expected',
        ),
      });
      if (changeRequest) {
        dispatch(metadataEditNewChangeRequestFromCopy(changeRequest));
      }
      return Promise.resolve();
    },
    [dispatch, getState, work],
  );

  const firstPreferredTitle = work?.preferredTitle?.[0] ?? '';

  const handleOpenSearch = useCallback(() => {
    context.setSourceField(
      {
        fieldId: 'searchPanel',
        fieldConfig: {
          titleKey: 'general.search',
          initialState: EntitySearchState.search,
          entity: Concept.work,
          sourceEntityId: work.id,
          canMove: !!manifestation,
          canCopy: true,
          allowedEntities: [Concept.work],
        },
        currentValue: {
          linkStatus: 'verified',
          entityId: work.id,
          type: Concept.work,
          name: firstPreferredTitle,
        },
      },
      (target, _fieldId, action) => {
        const targetWork = target as VerifiedLinkedCatalogPostLink | undefined;
        if (targetWork) {
          if (action === 'move') {
            handleMove(
              targetWork,
              assert(manifestation, 'useMoveToOrCopyFromOtherWork: required'),
            );
          } else if (action === 'copy') {
            handleCopy(targetWork, manifestation);
          } else {
            assert(
              false,
              'useMoveToOrCopyFromOtherWork: Unexpected move or copy action',
              {
                action,
              },
            );
          }
        }
      },
    );
  }, [
    context,
    handleCopy,
    handleMove,
    manifestation,
    work.id,
    firstPreferredTitle,
  ]);

  return useMemo(
    () => ({
      openSearch: handleOpenSearch,
      handleOpenSearch,
      ConfirmMoveToOther,
      isConfirmOpen: isOpen,
      isConfirmClosing: isClosing,
    }),
    [ConfirmMoveToOther, handleOpenSearch, isClosing, isOpen],
  );
};
